import React from 'react'
// components
import { Button, Menu } from 'components/UI'
import { Backdrop, Chip } from '@mui/material'
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined'
// constants
import { Modals, KPI_RESULT_TARGET_TYPE_VALUES, KPI_RESULT_TYPE_VALUES } from 'constants/index'
// context
import { KPIContext } from 'context/index'
// hooks
import { useShowModal } from 'hooks/index'
// themes
import { colors } from 'themes'
// icons
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined'
import SettingsIcon from '@mui/icons-material/SettingsOutlined'
// views
import {
  CardText,
  DotsIcon,
  CardHeader,
  CardWrapper,
  ChipWrapper,
  CardContent,
  ButtonWrapper
} from 'components/Modals/PlanItems/KPI/MinimizedModal/components/KPIResultsList/components/KPIResultCard/view'
import type { Moment } from 'moment'
// utils
import moment from 'moment'
import { isUuid } from 'utils/uuid'
import { safeGetOr } from 'utils'
import { getScaleOptionValue } from 'utils/kpi'
import { find, get, isUndefined, pipe } from 'lodash/fp'

interface Props {
  period: any
  deleteResult: (period: any) => any
  showSetResultModal: (period: any) => any
}

const KPIResultCard: React.FC<Props> = ({ period, deleteResult, showSetResultModal }) => {
  const [isHovered, setHovered] = React.useState(false)
  const [isZoomed, setZoomed] = React.useState(false)

  const { entity } = React.useContext(KPIContext) as any

  const { kpiModifyKPI, kpiDeleteKPIResult, kpiAddKPIResult, kpiModifyKPIResult } = entity.currentPermissions

  const { showModal } = useShowModal({ modalType: Modals.KPI_RESULTS_MODAL })

  if (!period.dueDate) {
    return null
  }

  // constants
  const startPoint: Moment = moment(period.startPoint)
  const lastDayOfThePeriod: Moment = moment(period.lastDayOfThePeriod)
  const dueDate: Moment = moment(period.dueDate)

  const dateChipLabel = dueDate.format('D MMMM')

  const isPast = dueDate.isBefore(moment(), 'day')
  const isToday = dueDate.isSame(moment(), 'day')
  const isTomorrow = dueDate.diff(moment(), 'days') === 0
  const isFuture = dueDate.isAfter(moment(), 'day')

  const capturedValue = pipe(safeGetOr<any, any, any>({}, 'capturedResult'), (result: any) =>
    getScaleOptionValue(result.value, entity.resultType, entity.resultTypeScale)
  )(period)

  const isValueEmpty = isUndefined(get('value', capturedValue))
  const isPastEmpty = isPast && isValueEmpty
  const isTodayEmpty = isToday && isValueEmpty
  const isFutureEmpty = isFuture && isValueEmpty

  const chipColor = isFutureEmpty
    ? colors.gray350
    : isTodayEmpty
      ? colors.orange300
      : isPastEmpty
        ? colors.red200
        : colors.green300

  const onMouseEnter = () => {
    if (isValueEmpty) {
      setHovered(true)
    }
  }

  const onMouseLeave = () => {
    if (isValueEmpty) {
      setHovered(false)
    }
  }

  // functionality which responsible for card background
  const getScaleValue = (value) => {
    if (entity.resultType === KPI_RESULT_TYPE_VALUES.SCALE || isUuid(value)) {
      return pipe(
        find((option: any) => option.id === value),
        get('value')
      )(entity.resultTypeScale)
    }
    return value
  }

  const results = safeGetOr<any, any, any>([], 'results')(period)

  const greaterComparator = pipe(
    find(({ type }) => type === KPI_RESULT_TARGET_TYPE_VALUES.GREATER),
    (result: any) => {
      if (result) {
        return { ...result, greaterValue: getScaleValue(result.greaterValue) }
      }
    }
  )(results)

  const betweenComparator = pipe(
    find(({ type }) => type === KPI_RESULT_TARGET_TYPE_VALUES.BETWEEN),
    (result: any) => {
      if (result) {
        return {
          ...result,
          betweenLower: getScaleValue(result.betweenLower),
          betweenUpper: getScaleValue(result.betweenUpper)
        }
      }
    }
  )(results)

  const lessComparator = pipe(
    find(({ type }) => type === KPI_RESULT_TARGET_TYPE_VALUES.LESS),
    (result: any) => {
      if (result) {
        return { ...result, lessValue: getScaleValue(result.lessValue) }
      }
    }
  )(results)

  const recordedValue = parseFloat(get('value', capturedValue))

  let headerBackground = colors.darkBlue500

  if (greaterComparator?.greaterValue && recordedValue >= greaterComparator.greaterValue) {
    headerBackground = greaterComparator.color
  }

  if (betweenComparator?.betweenUpper && betweenComparator?.betweenLower) {
    const betweenUpper = parseFloat(betweenComparator.betweenUpper)
    const betweenLower = parseFloat(betweenComparator.betweenLower)

    if (recordedValue > betweenLower && recordedValue < betweenUpper) {
      headerBackground = betweenComparator.color
    }
  }

  if (lessComparator?.lessValue && recordedValue <= lessComparator.lessValue) {
    headerBackground = lessComparator.color
  }

  const styles: React.CSSProperties = isZoomed ? { position: 'relative', zIndex: 2 } : {}

  const showResultTargetsModal = () => showModal()

  return (
    <>
      {isZoomed && <Backdrop open style={{ zIndex: 1 }} onClick={() => setZoomed(false)} />}

      <CardWrapper onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} style={styles}>
        <CardHeader backgroundColor={headerBackground}>
          {startPoint.format('D MMMM Y')} to {lastDayOfThePeriod.format('D MMMM Y')}
        </CardHeader>
        <CardContent>
          {!isValueEmpty && <CardText>{capturedValue.label}</CardText>}
          {isFutureEmpty && !isTomorrow && (
            <CardText>Due in {period.dueDate.diff?.(moment().startOf('day'), 'days')} days</CardText>
          )}
          {isFutureEmpty && isTomorrow && <CardText>Due Tomorrow</CardText>}
          {isTodayEmpty && <CardText>Due Today</CardText>}
          {isPastEmpty && <CardText>Overdue</CardText>}

          <ChipWrapper>
            <Chip
              label={dateChipLabel}
              icon={<WatchLaterOutlinedIcon style={{ color: colors.white }} />}
              style={{ backgroundColor: chipColor, color: colors.white, fontWeight: 500 }}
            />
          </ChipWrapper>

          {kpiAddKPIResult && isHovered && (
            <ButtonWrapper>
              <Button shadowLess onClick={showSetResultModal(period)}>
                Add Result
              </Button>
            </ButtonWrapper>
          )}

          {!isValueEmpty && (kpiModifyKPI || kpiModifyKPIResult) && (
            <Menu trigger={<DotsIcon htmlColor='black' />}>
              {kpiModifyKPIResult && <span onClick={showSetResultModal(period)}><EditIcon />Edit</span>}
              {kpiDeleteKPIResult && <span onClick={deleteResult(period)}><DeleteIcon />Delete</span>}
              {kpiModifyKPI && <span onClick={showResultTargetsModal}><SettingsIcon />Set Range</span>}
            </Menu>
          )}
        </CardContent>
      </CardWrapper>
    </>
  )
}

export default KPIResultCard
